'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { pathTo } from '@/lib/base-path';
import {
  TOUR_STEPS,
  persistTourStatus,
  type TourStep,
} from '@/lib/tour';

interface TourContextValue {
  active: boolean;
  startTour: () => void;
  skipTour: () => void;
}

const TourContext = createContext<TourContextValue | null>(null);

export function useTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useTour deve ser usado dentro de TourProvider');
  return ctx;
}

/** Versão segura para componentes que podem renderizar fora do provider. */
export function useTourOptional() {
  return useContext(TourContext);
}

function clamp(n: number, min: number, max: number) {
  return Math.min(max, Math.max(min, n));
}

function measureTarget(selector: string | undefined): DOMRect | null {
  if (!selector || typeof document === 'undefined') return null;
  const el = document.querySelector(`[data-tour="${selector}"]`);
  if (!el) return null;
  return el.getBoundingClientRect();
}

function TourTooltip({
  step,
  stepIndex,
  total,
  rect,
  onPrev,
  onNext,
  onSkip,
}: {
  step: TourStep;
  stepIndex: number;
  total: number;
  rect: DOMRect | null;
  onPrev: () => void;
  onNext: () => void;
  onSkip: () => void;
}) {
  const isCenter = !step.target || step.placement === 'center' || !rect;
  const pad = 12;
  const tooltipW = 320;

  let top = window.innerHeight / 2 - 80;
  let left = window.innerWidth / 2 - tooltipW / 2;

  if (!isCenter && rect) {
    const placement = step.placement ?? 'bottom';
    if (placement === 'bottom') {
      top = rect.bottom + pad;
      left = rect.left + rect.width / 2 - tooltipW / 2;
    } else if (placement === 'top') {
      top = rect.top - pad - 160;
      left = rect.left + rect.width / 2 - tooltipW / 2;
    } else if (placement === 'left') {
      top = rect.top + rect.height / 2 - 80;
      left = rect.left - pad - tooltipW;
    } else {
      top = rect.top + rect.height / 2 - 80;
      left = rect.right + pad;
    }
    left = clamp(left, pad, window.innerWidth - tooltipW - pad);
    top = clamp(top, pad, window.innerHeight - 200);
  }

  return (
    <div
      className="fixed z-[1102] w-[min(320px,calc(100vw-24px))] rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-2xl p-4"
      style={{ top, left }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-step-title"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Sparkles className="w-4 h-4 text-blue-600 dark:text-blue-400 shrink-0" aria-hidden />
          <p id="tour-step-title" className="font-semibold text-gray-900 dark:text-slate-100 text-sm">
            {step.title}
          </p>
        </div>
        <button
          type="button"
          onClick={onSkip}
          className="shrink-0 p-1 rounded text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
          aria-label="Fechar tour"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <p className="text-sm text-gray-600 dark:text-slate-300 leading-relaxed mb-4">{step.body}</p>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-gray-400 dark:text-slate-500">
          {stepIndex + 1} / {total}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSkip}
            className="px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
          >
            Pular tour
          </button>
          {stepIndex > 0 && (
            <button
              type="button"
              onClick={onPrev}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800"
            >
              <ChevronLeft className="w-3.5 h-3.5" /> Anterior
            </button>
          )}
          <button
            type="button"
            onClick={onNext}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium btn-primary rounded-lg"
          >
            {stepIndex >= total - 1 ? 'Concluir' : 'Próximo'}
            {stepIndex < total - 1 && <ChevronRight className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}

export function TourProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [active, setActive] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const pendingStart = useRef(false);

  const step = TOUR_STEPS[stepIndex];
  const total = TOUR_STEPS.length;

  const close = useCallback((status: 'completed' | 'skipped') => {
    persistTourStatus(status);
    setActive(false);
    setStepIndex(0);
    setRect(null);
    pendingStart.current = false;
  }, []);

  const refreshRect = useCallback(() => {
    if (!active) return;
    const current = TOUR_STEPS[stepIndex];
    setRect(measureTarget(current.target));
  }, [active, stepIndex]);

  const startTour = useCallback(() => {
    const home = pathTo('/') || '/';
    if (pathname !== home) {
      pendingStart.current = true;
      router.push(home);
      return;
    }
    setStepIndex(0);
    setActive(true);
  }, [pathname, router]);

  const skipTour = useCallback(() => close('skipped'), [close]);

  useEffect(() => {
    const home = pathTo('/') || '/';
    if (pendingStart.current && pathname === home) {
      pendingStart.current = false;
      setStepIndex(0);
      setActive(true);
    }
  }, [pathname]);

  useEffect(() => {
    if (!active) return;
    const current = TOUR_STEPS[stepIndex];
    const el = current.target
      ? document.querySelector(`[data-tour="${current.target}"]`)
      : null;
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    const t = window.setTimeout(refreshRect, el ? 350 : 0);
    return () => window.clearTimeout(t);
  }, [active, stepIndex, refreshRect]);

  useEffect(() => {
    if (!active) return;
    const onChange = () => refreshRect();
    window.addEventListener('resize', onChange);
    window.addEventListener('scroll', onChange, true);
    return () => {
      window.removeEventListener('resize', onChange);
      window.removeEventListener('scroll', onChange, true);
    };
  }, [active, refreshRect]);

  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close('skipped');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, close]);

  const goNext = () => {
    if (stepIndex >= total - 1) {
      close('completed');
      return;
    }
    setStepIndex((i) => i + 1);
  };

  const goPrev = () => setStepIndex((i) => Math.max(0, i - 1));

  const value = useMemo(
    () => ({ active, startTour, skipTour }),
    [active, startTour, skipTour],
  );

  const pad = 8;
  const highlightStyle =
    rect && step.target
      ? {
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
        }
      : null;

  return (
    <TourContext.Provider value={value}>
      {children}
      {active && step && (
        <>
          {!highlightStyle && <div className="fixed inset-0 z-[1100] bg-black/55" aria-hidden />}
          {highlightStyle ? (
            <div
              className="fixed z-[1101] rounded-lg ring-4 ring-blue-500/80 pointer-events-none transition-all duration-200"
              style={{
                ...highlightStyle,
                boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
              }}
            />
          ) : null}
          <TourTooltip
            step={step}
            stepIndex={stepIndex}
            total={total}
            rect={rect}
            onPrev={goPrev}
            onNext={goNext}
            onSkip={() => close('skipped')}
          />
        </>
      )}
    </TourContext.Provider>
  );
}
